// src/pages/PrivacyPage.jsx
import React from "react";
import { Helmet } from "react-helmet";

const SUPPORT_EMAIL = "artparcel.sup@gmail.com";      // TODO: replace
const COMPANY_NAME  = "ArtParcel";                     // TODO: replace if needed
const SITE_URL      = "https://artparcel.club";       // TODO: replace
const LAST_UPDATED  = "September 15, 2025";            // keep explicit date for clarity

const PrivacyPage = () => {
  return (
    <div className="bg-slate-900 text-white min-h-screen flex flex-col">
      <Helmet>
        <title>Privacy Policy | {COMPANY_NAME}</title>
        <meta name="description" content={`${COMPANY_NAME} Privacy Policy`} />
        <meta property="og:title" content={`Privacy Policy | ${COMPANY_NAME}`} />
        <meta property="og:description" content={`${COMPANY_NAME} Privacy Policy`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/privacy`} />
      </Helmet>

      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <header className="mb-10 text-center">
            <h1 className="text-4xl font-bold">Privacy Policy</h1>
            <p className="text-sm text-slate-400 mt-2">Last updated: {LAST_UPDATED}</p>
            <p className="text-xs text-slate-500 mt-1 italic">
              This Policy explains how we collect, use, and share information when you use {COMPANY_NAME}.
              It’s provided for general information and is not legal advice.
            </p>
          </header>

          {/* Table of Contents */}
          <nav className="mb-12 rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
            <h2 className="text-lg font-semibold mb-2">Table of Contents</h2>
            <ul className="text-sm text-slate-300 grid sm:grid-cols-2 gap-y-1 gap-x-6 list-disc list-inside">
              <li><a href="#scope" className="hover:text-white">1. Scope</a></li>
              <li><a href="#info-we-collect" className="hover:text-white">2. Information We Collect</a></li>
              <li><a href="#how-we-use" className="hover:text-white">3. How We Use Information</a></li>
              <li><a href="#legal-bases" className="hover:text-white">4. Legal Bases (EEA/UK)</a></li>
              <li><a href="#cookies" className="hover:text-white">5. Cookies & Analytics</a></li>
              <li><a href="#payments" className="hover:text-white">6. Payments & Pixel Drops</a></li>
              <li><a href="#sharing" className="hover:text-white">7. How We Share Information</a></li>
              <li><a href="#retention" className="hover:text-white">8. Data Retention</a></li>
              <li><a href="#security" className="hover:text-white">9. Security</a></li>
              <li><a href="#children" className="hover:text-white">10. Children’s Privacy</a></li>
              <li><a href="#rights" className="hover:text-white">11. Your Privacy Rights</a></li>
              <li><a href="#x-border" className="hover:text-white">12. International Transfers</a></li>
              <li><a href="#changes" className="hover:text-white">13. Changes to This Policy</a></li>
              <li><a href="#contact" className="hover:text-white">14. Contact Us</a></li>
            </ul>
          </nav>

          {/* Sections */}
          <section id="scope" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">1. Scope</h2>
            <p className="text-slate-300">
              This Privacy Policy applies to {COMPANY_NAME} websites, apps, and services that link to it
              (the “Service”). By using the Service, you consent to the practices described here.
            </p>
          </section>

          <section id="info-we-collect" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">2. Information We Collect</h2>
            <ul className="text-slate-300 list-disc list-inside space-y-2">
              <li>
                <span className="font-semibold">Account & Profile:</span> username, display name, email, avatar,
                social links (e.g., Twitter/Instagram), preferences.
              </li>
              <li>
                <span className="font-semibold">Usage & Activity:</span> interactions with the canvas, artworks created/edited,
                likes, comments/messages (if available), device and event logs (e.g., IP address, browser, OS, timestamps).
              </li>
              <li>
                <span className="font-semibold">Transactional:</span> token purchases or redemptions, order IDs, billing
                metadata from payment processors (we don’t store card details).
              </li>
              <li>
                <span className="font-semibold">Cookies & Similar Tech:</span> session identifiers, analytics, preferences (see{" "}
                <a href="#cookies" className="text-indigo-400 underline">Cookies & Analytics</a>).
              </li>
              <li>
                <span className="font-semibold">User Content:</span> pixel art, images you upload, artwork titles/descriptions,
                and related metadata you choose to share publicly.
              </li>
              <li>
                <span className="font-semibold">Support:</span> information you send to {SUPPORT_EMAIL} or forms (including attachments).
              </li>
            </ul>
          </section>

          <section id="how-we-use" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">3. How We Use Information</h2>
            <ul className="text-slate-300 list-disc list-inside space-y-2">
              <li>Operate and improve the Service: provide canvas features, save artworks, and keep things running smoothly.</li>
              <li>Process purchases and Pixel Drops; detect/prevent fraud or abuse (e.g., botting, multi-account misuse).</li>
              <li>Moderate content; enforce our <a href="/terms" className="text-indigo-400 underline">Terms</a> and rules.</li>
              <li>Analytics to understand usage and improve UX, performance, and reliability.</li>
              <li>Communicate with you: service notices, updates, security, and support responses.</li>
              <li>Comply with legal obligations and protect rights, safety, and property.</li>
            </ul>
          </section>

          <section id="legal-bases" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">4. Legal Bases (EEA/UK)</h2>
            <p className="text-slate-300 mb-2">
              Where GDPR/UK GDPR applies, we process personal data under these bases:
            </p>
            <ul className="text-slate-300 list-disc list-inside space-y-2">
              <li><span className="font-semibold">Contract:</span> to provide the Service and purchases you request.</li>
              <li><span className="font-semibold">Legitimate Interests:</span> e.g., security, moderation, analytics, product improvement.</li>
              <li><span className="font-semibold">Consent:</span> where required (e.g., certain cookies/marketing).</li>
              <li><span className="font-semibold">Legal Obligation:</span> to comply with applicable laws and requests.</li>
            </ul>
          </section>

          <section id="cookies" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">5. Cookies & Analytics</h2>
            <p className="text-slate-300 mb-2">
              We use cookies and similar technologies to keep you signed in, remember preferences, and perform analytics.
              You can manage cookies in your browser settings. Blocking some cookies may affect functionality.
            </p>
            <ul className="text-slate-300 list-disc list-inside space-y-2">
              <li><span className="font-semibold">Essential:</span> required for login, security, and core features.</li>
              <li><span className="font-semibold">Analytics:</span> to understand usage and improve performance (e.g., page views, feature adoption).</li>
              <li><span className="font-semibold">Preference:</span> theme, language, and in-app settings.</li>
            </ul>
          </section>

          <section id="payments" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">6. Payments & Pixel Drops</h2>
            <p className="text-slate-300 mb-2">
              Purchases are processed by third-party providers (e.g., Stripe). We do not store payment card numbers.
              Token balances are tied to your account and used for in-app features. See our{" "}
              <a href="/Terms/Refunds" className="text-indigo-400 underline">Refund Policy</a> for details.
            </p>
          </section>

          <section id="sharing" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">7. How We Share Information</h2>
            <ul className="text-slate-300 list-disc list-inside space-y-2">
              <li><span className="font-semibold">Service Providers:</span> hosting, storage, analytics, payments, customer support.</li>
              <li><span className="font-semibold">Legal & Safety:</span> to comply with law or protect rights, safety, and property.</li>
              <li><span className="font-semibold">Business Transfers:</span> in connection with a merger, acquisition, or asset sale.</li>
              <li><span className="font-semibold">Public Content:</span> artworks, usernames, and profiles you choose to make public may be visible to others.</li>
              <li className="text-slate-400 text-xs">
                We do not sell personal information in the traditional sense. If you are in a jurisdiction with “Do Not Sell or Share”
                rights (e.g., California), see <a href="#rights" className="text-indigo-400 underline">Your Privacy Rights</a>.
              </li>
            </ul>
          </section>

          <section id="retention" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">8. Data Retention</h2>
            <p className="text-slate-300">
              We keep personal data only as long as necessary for the purposes described here, to comply with legal
              obligations, resolve disputes, and enforce agreements. Retention periods vary by data type and use.
            </p>
          </section>

          <section id="security" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">9. Security</h2>
            <p className="text-slate-300">
              We use reasonable technical and organizational measures to protect personal data. No method of transmission
              or storage is 100% secure; risks remain.
            </p>
          </section>

          <section id="children" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">10. Children’s Privacy</h2>
            <p className="text-slate-300">
              The Service is not directed to children under 13 (or the minimum age in your jurisdiction). We don’t
              knowingly collect personal data from children. If you believe a child provided personal data, contact us at{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-indigo-400 underline">{SUPPORT_EMAIL}</a>.
            </p>
          </section>

          <section id="rights" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">11. Your Privacy Rights</h2>
            <p className="text-slate-300 mb-2">
              Depending on your location, you may have rights such as access, correction, deletion, portability, restriction,
              objection, and withdrawal of consent. To exercise rights, email{" "}
              <a href={`mailto:${SUPPORT_EMAIL}?subject=Privacy%20Request`} className="text-indigo-400 underline">
                {SUPPORT_EMAIL}
              </a>{" "}
              and include details to help us verify your identity and request.
            </p>
            <ul className="text-slate-300 list-disc list-inside space-y-2">
              <li>
                <span className="font-semibold">EEA/UK:</span> You may contact your local data protection authority.
              </li>
              <li>
                <span className="font-semibold">California (CCPA/CPRA):</span> You may have rights to know/access, delete,
                correct, and opt out of “sale”/“sharing” of personal information. Use our{" "}
                <a href="/do-not-sell" className="text-indigo-400 underline">Do Not Sell or Share</a>{" "}
                options where applicable, or email us.
              </li>
              <li>
                <span className="font-semibold">Appeals:</span> If we deny a request, you may have the right to appeal by replying
                to our decision email or contacting us again with “Appeal” in the subject.
              </li>
            </ul>
          </section>

          <section id="x-border" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">12. International Transfers</h2>
            <p className="text-slate-300">
              If we transfer personal data internationally, we rely on appropriate safeguards (e.g., standard contractual
              clauses) where required by law.
            </p>
          </section>

          <section id="changes" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">13. Changes to This Policy</h2>
            <p className="text-slate-300">
              We may update this Privacy Policy from time to time. Material changes will be posted here with an updated
              “Last updated” date. Your continued use of the Service means you accept the changes.
            </p>
          </section>

          <section id="contact" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">14. Contact Us</h2>
            <p className="text-slate-300">
              Questions or requests? Email{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-indigo-400 hover:text-indigo-300 underline">
                {SUPPORT_EMAIL}
              </a>.
            </p>
          </section>

          {/* Back to top */}
          <div className="mt-12 flex justify-center">
            <a
              href="#top"
              onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className="text-sm text-slate-300 hover:text-white underline"
            >
              Back to top
            </a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 bg-slate-900/80">
        <div className="max-w-6xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-sm text-slate-400">© {new Date().getFullYear()} {COMPANY_NAME}</div>
          <nav className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <a href="/Terms/Terms" className="text-slate-300 hover:text-white">Terms</a>
            <a href="/Terms/Privacy" className="text-slate-300 hover:text-white">Privacy</a>
            <a href="/Terms/Refunds" className="text-slate-300 hover:text-white">Refund Policy</a>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-slate-300 hover:text-white">{SUPPORT_EMAIL}</a>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
